import { useEffect, useState, useCallback, useRef } from 'react';
import { fetchUnitStatuses } from '@/lib/api';
import { useAuth } from './AuthContext';
import { UnitStatusContext, type StatusInfo, type UnitStatusContextType } from './UnitStatusContext';

/** Polling interval in milliseconds (15 seconds to match existing behavior) */
const POLL_INTERVAL_MS = 15_000;

/**
 * Provides real-time unit status data via polling.
 *
 * Polls /api/v1/units/statuses every 15 seconds and provides
 * status data to all consuming components. This ensures consistent
 * status display across Map, Units, and any other pages.
 *
 * Only polls when user is authenticated.
 */
export function UnitStatusProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [statuses, setStatuses] = useState<Map<string, StatusInfo>>(new Map());
  const [isPolling, setIsPolling] = useState(false);
  const [lastPollTime, setLastPollTime] = useState<Date | null>(null);
  const [pollError, setPollError] = useState<string | null>(null);

  // Use ref to track interval ID for cleanup
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Track if component is mounted to prevent state updates after unmount
  const mountedRef = useRef(true);

  /**
   * Fetch latest statuses and update state
   */
  const pollStatuses = useCallback(async () => {
    if (!mountedRef.current) return;

    try {
      const { statuses: statusArray } = await fetchUnitStatuses();

      if (!mountedRef.current) return;

      // Convert array to Map for O(1) lookups
      const statusMap = new Map<string, StatusInfo>();
      for (const item of statusArray) {
        statusMap.set(item.id, {
          status: item.status,
          soc: item.soc,
          lastUpdate: item.lastUpdate,
          warning_count: item.warning_count,
          fault_count: item.fault_count,
        });
      }

      setStatuses(statusMap);
      setLastPollTime(new Date());
      setPollError(null);
    } catch (error) {
      if (!mountedRef.current) return;

      const message = error instanceof Error ? error.message : 'Unknown error';
      console.error('[UnitStatusContext] Poll failed:', message);
      setPollError(message);
      // Don't clear statuses on error - keep stale data visible
    }
  }, []);

  /**
   * Start polling when authenticated
   * Use user?.id as dependency to prevent re-initializing polling on token refresh
   * (when user object reference changes but user ID stays the same)
   */
  useEffect(() => {
    mountedRef.current = true;

    // Only poll if authenticated
    if (!user) {
      setIsPolling(false);
      setStatuses(new Map());
      return;
    }

    // Initial fetch
    setIsPolling(true);
    pollStatuses();

    // Set up interval
    intervalRef.current = setInterval(pollStatuses, POLL_INTERVAL_MS);

    return () => {
      mountedRef.current = false;
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      setIsPolling(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, pollStatuses]);

  /**
   * Get status for a specific unit
   */
  const getStatus = useCallback((unitId: string): StatusInfo['status'] => {
    return statuses.get(unitId)?.status ?? 'unknown';
  }, [statuses]);

  /**
   * Get full status info for a unit
   */
  const getStatusInfo = useCallback((unitId: string): StatusInfo | null => {
    return statuses.get(unitId) ?? null;
  }, [statuses]);

  /**
   * Manual refresh (exposed for UI refresh buttons)
   */
  const refreshNow = useCallback(async () => {
    await pollStatuses();
  }, [pollStatuses]);

  // Calculate derived values
  const onlineCount = Array.from(statuses.values()).filter(s => s.status === 'online').length;
  const totalCount = statuses.size;

  const value: UnitStatusContextType = {
    statuses,
    isPolling,
    lastPollTime,
    pollError,
    getStatus,
    getStatusInfo,
    refreshNow,
    onlineCount,
    totalCount,
  };

  return (
    <UnitStatusContext.Provider value={value}>
      {children}
    </UnitStatusContext.Provider>
  );
}
